import { DuplicateIcon, MinusCircleIcon } from '@heroicons/react/solid';
import { addMinutes, differenceInMinutes, format, getISODay, parse, setDay, setISODay } from 'date-fns';
import { useState, useEffect, Fragment, useMemo } from 'react';
import { Input, Label } from '../Field';

const EMPTY_ARRAY = [];

function scheduleToDataset(schedule = []) {
    const dataset = Array.from(Array(7), () => []);

    schedule
        .filter((entry) => entry && entry.start && entry.duration)
        .forEach(({ start, duration, baseline_index }) => {
            start = new Date(start);
            const weekday = getISODay(start);
            const end = addMinutes(start, duration);
            const normalizedEntry = {
                start: format(start, 'HH:mm'),
                end: format(end, 'HH:mm'),
            };

            if (baseline_index !== undefined && baseline_index !== null && baseline_index !== '') {
                dataset[weekday - 1][parseInt(baseline_index)] = normalizedEntry;
            } else {
                dataset[weekday - 1].push(normalizedEntry);
            }
        });

    return dataset;
}

function restoreDeletedBaselineEntries(dataset, baselineDataset) {
    return dataset.map((entries, weekday) => {
        const baselineEntries = baselineDataset[weekday] || [];
        const restoredEntries = [];

        for (let index = 0; index < Math.max(entries.length, baselineEntries.length); index++) {
            if (entries[index]) {
                restoredEntries.push(entries[index]);
            } else if (baselineEntries[index]) {
                restoredEntries.push({ ...baselineEntries[index], deleted: true });
            }
        }

        return restoredEntries;
    });
}

function cloneDataset(dataset) {
    return dataset.map((weekday) => weekday.map((entry) => ({ ...entry })));
}

function schedulesMatchEntry(a, b) {
    return (a?.start || '') === (b?.start || '') && (a?.end || '') === (b?.end || '');
}

function isDeletedEntry(entry) {
    return !!entry?.deleted;
}

function hasInvalidTimeRange({ start, end }) {
    return !!start && !!end && end <= start;
}

function ScheduleTab({ schedule = EMPTY_ARRAY, baselineSchedule = EMPTY_ARRAY, t }) {
    const baselineDataset = useMemo(() => scheduleToDataset(baselineSchedule), [baselineSchedule]);
    const [dataset, setDataset] = useState(() => restoreDeletedBaselineEntries(scheduleToDataset(schedule), baselineDataset));

    const changeHandler = (weekday, index, field, value) => {
        setDataset((oldValue) => {
            const newValue = cloneDataset(oldValue);
            newValue[weekday][index][field] = value;
            return newValue;
        });
    };

    const removeHandler = (weekday, index) => {
        setDataset((oldValue) => {
            const newValue = cloneDataset(oldValue);

            if (baselineDataset[weekday][index]) {
                newValue[weekday][index].deleted = true;
            } else {
                newValue[weekday].splice(index, 1);
            }

            return newValue;
        });
    };

    const addHandler = (weekday) => {
        setDataset((oldValue) => {
            const newValue = cloneDataset(oldValue);
            newValue[weekday].push({
                start: '',
                end: '',
            });
            return newValue;
        });
    };

    const replicateMonday = () => {
        setDataset((oldValue) => {
            const newValue = cloneDataset(oldValue);
            const mondayEntries = newValue[0].filter((entry) => !isDeletedEntry(entry)).map(({ start, end }) => ({ start, end }));

            for (let i = 1; i <= 6; i++) {
                const baselineEntries = baselineDataset[i] || [];
                const replicatedEntries = mondayEntries.map((entry) => ({ ...entry }));

                for (let index = mondayEntries.length; index < baselineEntries.length; index++) {
                    replicatedEntries.push({ ...baselineEntries[index], deleted: true });
                }

                newValue[i] = replicatedEntries;
            }

            return newValue;
        });
    };

    const discardEntryChange = (weekday, index) => {
        setDataset((oldValue) => {
            const newValue = cloneDataset(oldValue);
            const baselineEntry = baselineDataset[weekday][index];

            if (baselineEntry) {
                newValue[weekday][index] = { ...baselineEntry };
            } else {
                newValue[weekday].splice(index, 1);
            }

            return newValue;
        });
    };

    useEffect(() => {
        setDataset(restoreDeletedBaselineEntries(scheduleToDataset(schedule), baselineDataset));
    }, [schedule, baselineDataset]);

    const slots = dataset
        .map((entries, index) =>
            entries
                .map(({ start, end }, baselineIndex) => {
                    if (entries[baselineIndex].deleted || !start || !end) {
                        return null;
                    }

                    const weekday = index + 1;
                    start = parse(start, 'HH:mm', setISODay(new Date(), weekday));
                    end = parse(end, 'HH:mm', setISODay(new Date(), weekday));

                    return {
                        baselineIndex,
                        start: start.toISOString(),
                        duration: differenceInMinutes(end, start),
                    };
                })
                .filter(Boolean)
        )
        .flat();

    return (
        <>
            <table>
                <tbody>
                    {dataset.map((hours, weekday) => {
                        const baselineHours = baselineDataset[weekday] || [];
                        const hasRemovedEntries = hours.some(isDeletedEntry);

                        return (
                            <tr key={weekday}>
                                <td className="flex items-center gap-2 pr-8 align-top capitalize">
                                    <Label>{format(setDay(new Date(), weekday + 1), 'iiii', { locale: window.dateLocale })}</Label>
                                    {hasRemovedEntries && <span className="h-2 w-2 rounded-full bg-amber-400" title={t('changed-field')} />}
                                    {weekday == 0 && (
                                        <button type="button" title={t('clone-schedule')} onClick={replicateMonday}>
                                            <DuplicateIcon className="h-4 text-gray-400 hover:text-gray-600" />
                                        </button>
                                    )}
                                </td>
                                <td className="pb-4">
                                    <div className="space-y-1">
                                        {hours.map((entry, index) => {
                                            const baselineEntry = baselineHours[index];
                                            const deleted = isDeletedEntry(entry);
                                            const startChanged = (entry.start || '') !== (baselineEntry?.start || '');
                                            const endChanged = (entry.end || '') !== (baselineEntry?.end || '');
                                            const entryChanged = deleted || !schedulesMatchEntry(entry, baselineEntry);
                                            const invalidTimeRange = hasInvalidTimeRange(entry);
                                            const changedInputClasses = 'border-amber-400 bg-amber-50 ring-1 ring-amber-200 focus:border-amber-500 focus:ring-amber-500';
                                            const invalidInputClasses = 'border-red-400 bg-red-50 ring-1 ring-red-200 focus:border-red-500 focus:ring-red-500';

                                            if (deleted) {
                                                return (
                                                    <div key={index} className="flex items-center gap-1 text-xs text-amber-700">
                                                        <div className="flex items-center gap-1 rounded-md border border-dashed border-amber-300 bg-amber-50 px-2 py-1">
                                                            <span className="line-through">{entry.start}</span>
                                                            <span>-</span>
                                                            <span className="line-through">{entry.end}</span>
                                                        </div>
                                                        <div className="ml-2 w-24 flex-none">
                                                            <button
                                                                onClick={() => {
                                                                    discardEntryChange(weekday, index);
                                                                }}
                                                                type="button"
                                                                className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                                            >
                                                                {t('discard-change')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={index} className="flex items-center gap-1">
                                                    <div>
                                                        <div className="flex items-center gap-1">
                                                            <Input
                                                                type="time"
                                                                className={`${startChanged ? changedInputClasses : ''} py-1 text-xs`}
                                                                value={entry.start}
                                                                onChange={(e) => {
                                                                    changeHandler(weekday, index, 'start', e.target.value);
                                                                }}
                                                                title={startChanged ? t('changed-field') : undefined}
                                                                required
                                                            />
                                                            <Input
                                                                type="time"
                                                                className={`${invalidTimeRange ? invalidInputClasses : endChanged ? changedInputClasses : ''} py-1 text-xs`}
                                                                value={entry.end}
                                                                onChange={(e) => {
                                                                    changeHandler(weekday, index, 'end', e.target.value);
                                                                }}
                                                                ref={(input) => {
                                                                    input?.setCustomValidity(invalidTimeRange ? t('end-after-start') : '');
                                                                }}
                                                                title={invalidTimeRange ? t('end-after-start') : endChanged ? t('changed-field') : undefined}
                                                                required
                                                            />
                                                        </div>
                                                        {invalidTimeRange && <p className="mt-1 text-xs text-red-600">{t('end-after-start')}</p>}
                                                    </div>
                                                    <div className="ml-2 flex w-24 flex-none items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                removeHandler(weekday, index);
                                                            }}
                                                            type="button"
                                                            className="text-gray-400 hover:text-gray-700"
                                                        >
                                                            <MinusCircleIcon className="h-5 w-5" />
                                                        </button>
                                                        {entryChanged && (
                                                            <button
                                                                onClick={() => {
                                                                    discardEntryChange(weekday, index);
                                                                }}
                                                                type="button"
                                                                className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                                            >
                                                                {t('discard-change')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <button
                                        onClick={() => {
                                            addHandler(weekday);
                                        }}
                                        type="button"
                                        className="text-xs text-gray-500 hover:text-gray-800"
                                    >
                                        + {t('add-schedule')}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {slots.length == 0 && <input type="hidden" name={`schedule[]`} />}
            {slots.map(({ baselineIndex, start, duration }, index) => (
                <Fragment key={index}>
                    <input type="hidden" name={`_schedule_baseline_indexes[${index}]`} value={baselineIndex} readOnly />
                    <input type="hidden" name={`schedule[${index}][start]`} value={start} readOnly />
                    <input type="hidden" name={`schedule[${index}][duration]`} value={duration} readOnly />
                </Fragment>
            ))}
        </>
    );
}

export default ScheduleTab;
